// src/components/layout/PortfolioLayout.jsx
import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Menu, X, ExternalLink, Mail, Phone, Linkedin, Zap } from 'lucide-react';
import SEOLayout from './SEOLayout';
import Newsletter from "../Newsletter";
import CookieConsent from '../CookieConsent';

const PortfolioLayout = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();

    const isActive = (path) => {
        if (path === '/' && location.pathname === '/') {
            return true;
        }
        if (path !== '/' && location.pathname.startsWith(path)) {
            return true;
        }
        return false;
    };

    const navigation = [
        { name: 'Home', href: '/' },
        { name: 'About', href: '/about' },
        { name: 'Solutions', href: '/portfolio' },
        { name: 'Contact', href: '/contact' }
    ];

    return (
        <SEOLayout>
            <div className="flex flex-col min-h-screen">
                {/* Header */}
                <header className="bg-gray-900 shadow-lg sticky top-0 z-40" role="banner">
                    <nav
                        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
                        aria-label="Main navigation"
                        role="navigation"
                    >
                        <div className="w-full py-4 flex items-center justify-between">
                            <div className="flex items-center">
                                <Link
                                    to="/"
                                    className="flex items-center space-x-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded"
                                    aria-label="Baraqu LLC - Home"
                                >
                                    <Zap className="h-8 w-8 text-blue-400" />
                                    <span className="text-2xl font-light">
                                        <span className="text-white">Baraqu</span>
                                        <span className="text-blue-400 font-normal"> LLC</span>
                                    </span>
                                </Link>

                                <div className="hidden ml-10 space-x-8 lg:block">
                                    {navigation.map((item) => (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            className={`text-base font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded px-3 py-2 ${
                                                isActive(item.href)
                                                    ? 'text-blue-400 bg-gray-800'
                                                    : 'text-gray-300 hover:text-white hover:bg-gray-800'
                                            }`}
                                            aria-current={isActive(item.href) ? 'page' : undefined}
                                        >
                                            {item.name}
                                        </Link>
                                    ))}
                                </div>
                            </div>

                            {/* Desktop CTA */}
                            <div className="hidden lg:flex lg:items-center space-x-4">
                                <a
                                    href="mailto:solutions@baraqu.com"
                                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 transition-colors duration-200"
                                    aria-label="Email Baraqu LLC"
                                >
                                    <Mail className="h-4 w-4 mr-2" aria-hidden="true" />
                                    Get Started
                                </a>
                            </div>

                            {/* Mobile menu button */}
                            <div className="lg:hidden">
                                <button
                                    type="button"
                                    className="bg-gray-800 rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-400"
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    aria-expanded={mobileMenuOpen}
                                    aria-controls="mobile-menu"
                                    aria-label="Toggle navigation menu"
                                >
                                    <span className="sr-only">Open menu</span>
                                    {mobileMenuOpen ? (
                                        <X className="h-6 w-6" aria-hidden="true" />
                                    ) : (
                                        <Menu className="h-6 w-6" aria-hidden="true" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Mobile menu */}
                        <div
                            className={`lg:hidden ${mobileMenuOpen ? 'block' : 'hidden'}`}
                            id="mobile-menu"
                        >
                            <div className="pt-2 pb-4 space-y-1" role="menu">
                                {navigation.map((item) => (
                                    <Link
                                        key={item.name}
                                        to={item.href}
                                        className={`block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                                            isActive(item.href)
                                                ? 'bg-gray-800 text-blue-400'
                                                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                                        }`}
                                        role="menuitem"
                                        onClick={() => setMobileMenuOpen(false)}
                                        aria-current={isActive(item.href) ? 'page' : undefined}
                                    >
                                        {item.name}
                                    </Link>
                                ))}
                                <div className="pt-4 border-t border-gray-700">
                                    <a
                                        href="mailto:solutions@baraqu.com"
                                        className="block px-3 py-2 rounded-md text-base font-medium bg-blue-600 text-white hover:bg-blue-700 text-center focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900"
                                        aria-label="Email Baraqu LLC"
                                    >
                                        Get Started
                                    </a>
                                </div>
                            </div>
                        </div>
                    </nav>
                </header>

                {/* Main Content */}
                <main id="main-content" className="flex-grow" role="main">
                    <Outlet />
                </main>

                {/* Footer */}
                <footer className="bg-gray-900 text-white" role="contentinfo">
                    <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                            {/* Company Section */}
                            <div className="md:col-span-2">
                                <div className="flex items-center space-x-2 mb-4">
                                    <Zap className="h-8 w-8 text-blue-400" />
                                    <h3 className="text-2xl font-light">
                                        <span className="text-white">Baraqu</span>
                                        <span className="text-blue-400 font-normal"> LLC</span>
                                    </h3>
                                </div>
                                <p className="text-gray-300 mb-6 leading-relaxed">
                                    Technology solutions that flash like lightning and endure like stone.
                                    From the first recorded data to tomorrow's innovations, we bridge 5,000 years
                                    of systematic human progress.
                                </p>
                                <p className="text-gray-400 text-sm mb-6">
                                    <strong>barāqu</strong> (Sumerian): "to flash like lightning, to shine"
                                </p>
                                <div className="flex space-x-4">
                                    <a
                                        href="mailto:solutions@baraqu.com"
                                        className="text-gray-300 hover:text-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded transition-colors duration-200"
                                        aria-label="Email Baraqu LLC"
                                    >
                                        <Mail className="h-6 w-6" aria-hidden="true" />
                                    </a>
                                    <a
                                        href="tel:+13105702674"
                                        className="text-gray-300 hover:text-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded transition-colors duration-200"
                                        aria-label="Call Baraqu LLC"
                                    >
                                        <Phone className="h-6 w-6" aria-hidden="true" />
                                    </a>
                                    <a
                                        href="https://linkedin.com/company/baraqu-llc"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-gray-300 hover:text-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded transition-colors duration-200"
                                        aria-label="Connect with Baraqu LLC on LinkedIn"
                                    >
                                        <Linkedin className="h-6 w-6" aria-hidden="true" />
                                    </a>
                                </div>
                            </div>

                            {/* Solutions Section */}
                            <nav aria-labelledby="solutions-heading">
                                <h3 id="solutions-heading" className="text-lg font-medium mb-4 text-blue-400">Solutions</h3>
                                <ul className="space-y-2">
                                    <li>
                                        <Link
                                            to="/portfolio"
                                            className="text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded transition-colors duration-200"
                                        >
                                            Immersive Experiences
                                        </Link>
                                    </li>
                                    <li>
                                        <Link
                                            to="/portfolio"
                                            className="text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded transition-colors duration-200"
                                        >
                                            Specialized Hardware
                                        </Link>
                                    </li>
                                    <li>
                                        <Link
                                            to="/portfolio"
                                            className="text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded transition-colors duration-200"
                                        >
                                            Data Systems
                                        </Link>
                                    </li>
                                    <li>
                                        <Link
                                            to="/portfolio"
                                            className="text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded transition-colors duration-200"
                                        >
                                            System Integration
                                        </Link>
                                    </li>
                                </ul>
                            </nav>

                            {/* Ecosystem Section */}
                            <nav aria-labelledby="ecosystem-heading">
                                <h3 id="ecosystem-heading" className="text-lg font-medium mb-4 text-blue-400">Ecosystem</h3>
                                <ul className="space-y-2">
                                    <li>
                                        <a
                                            href="https://xrmgr.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded inline-flex items-center transition-colors duration-200"
                                        >
                                            XRMGR
                                            <ExternalLink className="ml-1 h-4 w-4" aria-hidden="true" />
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="https://postmgr.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded inline-flex items-center transition-colors duration-200"
                                        >
                                            PostMGR
                                            <ExternalLink className="ml-1 h-4 w-4" aria-hidden="true" />
                                        </a>
                                    </li>
                                </ul>
                            </nav>
                        </div>

                        {/* Newsletter Section */}
                        <div className="mt-12 pt-8 border-t border-gray-800">
                            <Newsletter variant="footer" />
                        </div>

                        {/* Bottom Footer */}
                        <div className="border-t border-gray-800 mt-8 pt-8">
                            <div className="flex flex-col md:flex-row justify-between items-center">
                                <p className="text-gray-400 text-sm">
                                    &copy; {new Date().getFullYear()} Baraqu LLC. All rights reserved.
                                </p>
                                <div className="flex space-x-6 mt-4 md:mt-0">
                                    <Link
                                        to="/privacy"
                                        className="text-gray-400 hover:text-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded transition-colors duration-200"
                                    >
                                        Privacy Policy
                                    </Link>
                                    <Link
                                        to="/terms"
                                        className="text-gray-400 hover:text-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-900 rounded transition-colors duration-200"
                                    >
                                        Terms of Service
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </footer>
            </div>
            <CookieConsent />
        </SEOLayout>
    );
};

export default PortfolioLayout;